import React, { useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { prominenceLabelText } from '../../utils/search/search-results-util/search-results.util'
import { useSearchType } from '../../hooks/search-type/search-type'

import {
    StyledListingStandout,
    StyledSearchListing,
    StyledSearchListingAdvert,
    StyledSearchListingAdvertColumn,
} from './search-listing.styles'

import { buildFpaUrl } from './fpa-link.util'
import { useScrollAdvertIntoView } from '../../hooks/use-scroll-advert-into-view'
import { observeResultsScroll } from '../../pages/search-results/results-scrolling-observer'
import styled from 'styled-components'

import { SearchListingDetails } from '../../components/search-listing-details/search-listing-details'
import { CarouselImages } from '../../components/carousel-images/carousel-images'
import { SellerType } from '../../__generated__/fed-graphql-types'
import { StyledCarouselBadge } from '../../components/carousel-images/carousel-images.styles'
import { calculateGridPosition } from '../../utils/tracking/grid-position'
import { useTrackViewedInteraction } from '../../hooks/tracking/useTrackViewedInteraction.hook'
import { SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing } from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { BadgeGroup, BadgeCategories } from '@at/design-system-elements'
import { useChannel, useFlexibleFinanceFilters } from '../../hooks/use-abtest.hook'

const NoHeightDiv = styled.div`
    position: absolute;
`
interface SearchListingProps {
    listing: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing
    index: number
    saveAdvertIsSaved?: boolean
    isGridDesign?: boolean
}

export const SearchListing = ({ listing, index, saveAdvertIsSaved = false }: SearchListingProps) => {
    const {
        advertId,
        badges,
        discount,
        finance,
        fpaLink,
        images,
        price,
        rrp,
        sellerType,
        sellerName,
        subTitle,
        title,
        type,
        attentionGrabber,
        trackingContext,
        hasDigitalRetailing,
        vehicleLocation,
        location,
        formattedDistance,
        numberOfImages,
        dealerReview,
        preReg,
    } = listing

    const {
        location: { search },
    } = useHistory()

    const channel = useChannel()

    const { isFinanceSearch } = useSearchType()
    const isFlexibleFinance = useFlexibleFinanceFilters()

    const prominenceLabel = prominenceLabelText(type)

    const url = buildFpaUrl(
        fpaLink,
        search,
        channel,
        false,
        listing.trackingContext.card.pageNumber,
        finance?.rawFinanceFields?.term,
        finance?.rawFinanceFields?.deposit,
        finance?.rawFinanceFields?.annualMileage
    )

    const isBrandNew = listing.trackingContext.advertContext?.condition === 'new'

    const scrollToRef = useScrollAdvertIntoView()

    const trackInteraction = useTrackViewedInteraction('search_card')

    const searchListingGridRef = useRef<HTMLDivElement | null>(null)

    const trackingElementRef = useCallback(
        (trackingElementRef: HTMLDivElement) => {
            observeResultsScroll(trackingElementRef, type, trackInteraction, {
                ...trackingContext,
                gridPosition: calculateGridPosition(trackingElementRef, trackingContext.card.position),
            })
        },
        [type, trackInteraction, trackingContext]
    )

    const getBadge = () => {
        if (hasDigitalRetailing) {
            return (
                <StyledCarouselBadge data-testid="reserve-online">
                    <BadgeGroup
                        content={[
                            {
                                category: BadgeCategories.onContent,
                                text: 'Reserve online',
                            },
                        ]}
                    />
                </StyledCarouselBadge>
            )
        }
        if (sellerType === SellerType.PRIVATE) {
            return (
                <StyledCarouselBadge data-testid="private-seller">
                    <BadgeGroup
                        content={[
                            {
                                category: BadgeCategories.onContent,
                                text: 'Private seller',
                            },
                        ]}
                    />
                </StyledCarouselBadge>
            )
        }
    }

    return (
        <StyledSearchListing id={advertId} ref={scrollToRef}>
            {prominenceLabel && (
                <StyledListingStandout as="span" data-testid={type} $capitalise={prominenceLabel === 'ad'}>
                    {prominenceLabel}
                </StyledListingStandout>
            )}

            <StyledSearchListingAdvert as="div" data-testid={`advertCard-${index}`}>
                <CarouselImages
                    images={images}
                    title={title}
                    advertId={advertId}
                    saveAdvertIsSaved={saveAdvertIsSaved}
                    badge={getBadge()}
                    numberOfImages={numberOfImages}
                    isLeasing={false}
                    fpaUrl={url}
                    type={type}
                    listingRef={searchListingGridRef.current || undefined}
                    trackingContext={trackingContext}
                />

                <StyledSearchListingAdvertColumn ref={searchListingGridRef} $isFlexibleFinance={isFlexibleFinance}>
                    <SearchListingDetails
                        advertId={advertId || ''}
                        financeSearch={isFinanceSearch}
                        fpaUrl={url}
                        monthlyPaymentInWholePounds={finance?.monthlyPrice.priceFormattedAndRounded}
                        initialPayment={finance?.initialPayment}
                        termMonths={finance?.termMonths}
                        price={price}
                        quoteType={finance?.quoteSubType}
                        rrpPrice={rrp}
                        saving={discount}
                        subtitle={subTitle}
                        title={title}
                        trackingContext={trackingContext}
                        type={type}
                        badges={badges}
                        vehicleLocation={vehicleLocation}
                        location={location}
                        formattedDistance={formattedDistance}
                        attentionGrabber={attentionGrabber}
                        representativeValues={finance?.representativeValues}
                        disclaimerText={finance?.disclaimerText}
                        financeProvider={finance?.financeListingProvider}
                        searchListingGridRef={searchListingGridRef.current || undefined}
                        dealerReview={dealerReview || undefined}
                        preReg={preReg}
                        isBrandNew={isBrandNew}
                        sellerType={sellerType}
                        sellerName={sellerName}
                    />
                </StyledSearchListingAdvertColumn>
            </StyledSearchListingAdvert>
            <NoHeightDiv ref={trackingElementRef} />
        </StyledSearchListing>
    )
}
